import React, { useMemo, useRef, useEffect, useState } from "react";
import Select from "react-select";
import ModuleLight from "./moduleLight";
import SingleLight from "./singleLight";

const LightBarPreviewComponent = ({ lightData, closeLightbarPreviewModel, tooltipData }) => {
  let lightbar = tooltipData.replace(/<br \/>/gi, "<br>");
  lightbar = lightbar.replace(/<br\/>/gi, "<br>");
  lightbar = lightbar.split("<br>");
  const intensityOptions = [
    { value: "high", label: "High" },
    { value: "steady", label: "Steady" },
    { value: "zero", label: "High" },
  ];
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);

  // calculate bounds
  const { maxX, maxY } = useMemo(
    () => ({
      maxX: Math.max(...lightData.map((l) => +l.left + +l.width)),
      maxY: Math.max(...lightData.map((l) => +l.top + +l.height)),
    }),
    [lightData]
  );

  const sortedLightData = lightData
    .sort((a, b) => +a.lightheadPosition - +b.lightheadPosition)
    .filter((light) => light.angle !== "90" && light.angle !== "270");
  const topLightheads = sortedLightData.filter((lighthead) => lighthead.top === "0");

  // calculate scale to fit container
  useEffect(() => {
    const resize = () => {
      if (!containerRef.current) return;

      const { offsetWidth, offsetHeight } = containerRef.current;
      const scaleX = offsetWidth / maxX;
      const scaleY = offsetHeight / maxY;

      // maintain aspect ratio
      const newScale = Math.min(scaleX, scaleY) * 0.65;
      if (lightData.length <= 8 && newScale > 15) {
        setScale(15);
        return;
      }
      setScale(newScale);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [maxX, maxY]);

  const moduleOutputs =
    lightData.filter((light) => light.moduleOutputs && Array.isArray(light.moduleOutputs)).length > 0;

  return (
    <>
      <div id="previewLightbar-modal" className="m-auto">
        <div className="modal-dialog m-0">
          <div className="modal-content">
            <div className="d-flex align-items-center">
              <h2 className="modal-title text-white text-uppercase mt-0">PREVIEW</h2>
              <div className="close-icon text-end ms-auto">
                <button type="button" className="close" data-dismiss="modal" onClick={closeLightbarPreviewModel}>
                  <span aria-hidden="true">
                    <i className="fa fa-times text-red" />
                  </span>
                </button>
              </div>
            </div>
            <div>
              {lightbar.map((data) => (
                <div className="text-white mt-10">{data}</div>
              ))}
            </div>
            <div
              className="canvas-position mb-5"
              ref={containerRef}
              style={{
                padding: "1.5rem 0", // spacing inside
              }}
            >
              {/* Scaled Canvas, In case of Freedom V data needs recalculation */}
              <div
                style={{
                  position: "relative",
                  width: maxX * scale - (moduleOutputs ? 40 : 0),
                  height: maxY * scale + (moduleOutputs ? 20 : 0),
                  margin: "0 auto",
                  display: "flex",
                }}
              >
                {lightData.map((light) =>
                  light.moduleOutputs ? (
                    <ModuleLight
                      key={light.outputId}
                      light={light}
                      lightData={lightData}
                      topLightheads={topLightheads}
                      scale={scale}
                    />
                  ) : (
                    <SingleLight key={light.outputId} light={light} scale={scale} />
                  )
                )}
              </div>
            </div>
            <div className="table-responsive" style={{ height: "calc(100vh - 432px)" }}>
              <table className="table el-header table-bordered align-middle table-dark">
                <thead>
                  <tr>
                    <th>Lighthead Position</th>
                    <th>Source</th>
                    <th>Position</th>
                    <th>Calling Intensity</th>
                    <th>Blocking Intensity</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { pos: 2, source: "F5LBA - BLUE", location: "FRONT" },
                    { pos: "", source: "F5LBA - AMBER", location: "FRONT" },

                    { pos: 3, source: "F5LBA - BLUE", location: "FRONT" },
                    { pos: "", source: "F5LBA - AMBER", location: "FRONT" },

                    { pos: 4, source: "F5RBW - BLUE", location: "FRONT" },
                    { pos: "", source: "F5RBW - WHITE", location: "FRONT" },

                    { pos: 5, source: "F5RBW - BLUE", location: "FRONT" },
                    { pos: "", source: "F5RBW - WHITE", location: "FRONT" },

                    { pos: 7, source: "F5ERB - RED", location: "RIGHT" },
                    { pos: "", source: "F5ERB - BLUE", location: "RIGHT" },

                    { pos: 9, source: "F5RBA - BLUE", location: "REAR" },
                    { pos: "", source: "F5RBA - AMBER", location: "REAR" },

                    { pos: 10, source: "F5RBA - BLUE", location: "REAR" },
                    { pos: "", source: "F5RBA - AMBER", location: "REAR" },

                    { pos: 11, source: "F5LBWG - BLUE", location: "REAR" },
                    { pos: "", source: "F5LBWG - WHITE", location: "REAR" },
                    { pos: "", source: "F5LBWG - GREEN", location: "REAR" },

                    { pos: 12, source: "F5LBWG - BLUE", location: "REAR" },
                    { pos: "", source: "F5LBWG - WHITE", location: "REAR" },
                    { pos: "", source: "F5LBWG - GREEN", location: "REAR" },

                    { pos: 14, source: "F5ERB - RED", location: "LEFT" },
                    { pos: "", source: "F5ERB - BLUE", location: "LEFT" },
                  ].map((row) => (
                    <tr key={`${row.pos}-${row.source}-${row.location}`}>
                      <td>{row.pos}</td>
                      <td>{row.source}</td>
                      <td>{row.location}</td>

                      <td style={{ minWidth: "180px" }}>
                        <div className="form-group cursor-pt mb-0">
                          <Select
                            value={intensityOptions[2]}
                            options={intensityOptions}
                            className="select-sdd"
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                      </td>
                      <td style={{ minWidth: "180px" }}>
                        <div className="form-group cursor-pt mb-0">
                          <Select
                            value={intensityOptions[2]}
                            options={intensityOptions}
                            className="select-sdd"
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LightBarPreviewComponent;
